import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n/lingua.js';
import { chiama } from './cliente.js';

/**
 * Gli avvisi sugli ordini.
 *
 * Uno per ordine, non cinquemila. Nel v2 ce ne sono 9.938 di cui 3.378
 * diversi, e su un ordine solo — il PO03-37748 — cinquemila copie della
 * stessa notizia ripetuta per due anni.
 */

export interface Avviso {
  id: number;
  orderId: number;
  orderNumber: string | null;
  customer: string | null;
  type: 'eta_deviation_7_days' | 'order_state_downgrade';
  description: string;
  previousEstimatedArrivalDate: string | null;
  currentEstimatedArrivalDate: string | null;
  /** Quante volte lo stesso scostamento si è ripresentato. */
  seenCount: number;
  lastSeenAt: string;
  notifiedAt: string | null;
  resolvedAt?: string | null;
  createdAt: string;
}

export interface Invio {
  id: number;
  templateAlias: string | null;
  recipient: string;
  orderId: number | null;
  sentAt: string;
  succeeded: boolean;
  responseCode: string | null;
  error: string | null;
}

/** `attivo` falso per chi non vede gli ordini: gli avvisi sono loro. */
export function useAvvisi(attivo = true) {
  return useQuery({
    queryKey: ['avvisi'],
    queryFn: () =>
      chiama<{ alerts: Avviso[]; total: number; canCloseAll: boolean }>('/api/alerts'),
    enabled: attivo,
  });
}

export function useAvvisiOrdine(ordineId: number) {
  return useQuery({
    queryKey: ['avvisi-ordine', ordineId],
    queryFn: () => chiama<{ alerts: Avviso[] }>(`/api/orders/${ordineId}/alerts`),
    enabled: Number.isInteger(ordineId) && ordineId > 0,
  });
}

export function useChiudiAvviso() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      chiama<{ alerts: Avviso[]; total: number }>(`/api/alerts/${id}/close`, { metodo: 'POST' }),
    onSuccess: (dati) => {
      // chi può chiuderli tutti non cambia chiudendone uno
      client.setQueryData(['avvisi'], (prima: object | undefined) => ({ ...prima, ...dati }));
      void client.invalidateQueries({ queryKey: ['avvisi-ordine'] });
    },
  });
}

/**
 * Chiude tutti gli avvisi aperti dell'azienda in cui si sta lavorando.
 *
 * La risposta porta quanti ne ha chiusi: senza quel numero il pulsante
 * svuoterebbe una lista senza dire cosa è successo.
 */
export function useChiudiTuttiGliAvvisi() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      chiama<{ closed: number; alerts: Avviso[]; total: number }>('/api/alerts/close-all', {
        metodo: 'POST',
      }),
    onSuccess: (dati) => {
      client.setQueryData(['avvisi'], (prima: object | undefined) => ({
        ...prima,
        alerts: dati.alerts,
        total: dati.total,
      }));
      void client.invalidateQueries({ queryKey: ['avvisi-ordine'] });
    },
  });
}

export function useInvii() {
  return useQuery({
    queryKey: ['invii-posta'],
    queryFn: () => chiama<{ deliveries: Invio[]; sending: boolean }>('/api/mail/deliveries'),
  });
}

export interface ModelloPosta {
  alias: string;
  kind: string | null;
  language: 'it' | 'en';
  subject: string;
  /** Falso finché nessuno ha riletto il testo: i testi inglesi vengono da Google Translate. */
  reviewed: boolean;
}

export function useModelliPosta() {
  return useQuery({
    queryKey: ['modelli-posta'],
    queryFn: () => chiama<{ templates: ModelloPosta[] }>('/api/mail/templates'),
    staleTime: 10 * 60_000,
  });
}

export const ETICHETTE_AVVISO: Record<string, string> = {
  eta_deviation_7_days: t('Arrivo spostato'),
  order_state_downgrade: t('Stato tornato indietro'),
};
